import { useEffect } from 'react'

import { foregroundTripleForHex, hexToHslTriple, softTripleFromHex } from './color'

type Branding = { primaryColor: string; logoUrl: string | null } | undefined

/** The system-wide fallback the backend resolves to when a tenant hasn't
 *  customized branding (see TenantSettingsService.DEFAULT_PRIMARY_COLOR).
 *  Its exact HSL conversion (217 62% 33%) doesn't match the hand-picked
 *  static default already baked into index.css (220 62% 33% — a rounded
 *  approximation, not an exact conversion), so we treat this value as
 *  "no override" rather than apply a subtly different hue for the vast
 *  majority of tenants who haven't set a custom color. */
const SYSTEM_DEFAULT_HEX = '#204887'

const VARS = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--brand',
  '--brand-foreground',
  '--brand-soft',
] as const

/** Applies a tenant's branding.primaryColor to the live document as CSS
 *  custom property overrides, so the whole app (chrome + shadcn tokens)
 *  retints without a rebuild. Resets to the static index.css defaults —
 *  via removeProperty, not by writing navy values — whenever branding is
 *  absent, matches the system default, or on unmount (e.g. logout). */
export function useTenantTheme(branding: Branding) {
  const color = branding?.primaryColor

  useEffect(() => {
    const root = document.documentElement
    const isCustom = !!color && color.trim().toLowerCase() !== SYSTEM_DEFAULT_HEX
    const triple = isCustom ? hexToHslTriple(color!) : null

    if (!triple) {
      VARS.forEach((v) => root.style.removeProperty(v))
      return
    }

    const fg = foregroundTripleForHex(color!)
    const soft = softTripleFromHex(color!) ?? '220 100% 95%'

    root.style.setProperty('--primary', triple)
    root.style.setProperty('--primary-foreground', fg)
    root.style.setProperty('--ring', triple)
    root.style.setProperty('--brand', triple)
    root.style.setProperty('--brand-foreground', fg)
    root.style.setProperty('--brand-soft', soft)

    return () => VARS.forEach((v) => root.style.removeProperty(v))
  }, [color])
}
